import { setupLogoutHandler } from './handle-logout';

type CbarShellElement = HTMLElement & {
  loading: boolean;
  showDrawer: boolean;
  loggedIn: boolean;
  showNotifications: boolean;
  showChat: boolean;
  showServiceWorkerToast: boolean;
  showOfflineToast: boolean;
  drawerOpen: boolean;
  user: { profile: { name: string; picture: string } } | null;
  header?: {
    searchPlaceholder?: string;
  };
};

function isThemeApplied(): boolean {
  return Boolean(
    getComputedStyle(document.documentElement).getPropertyValue('--cbar-sys-color-primary').trim()
  );
}

export function waitForThemeApplied(timeoutMs = 5000): Promise<void> {
  if (isThemeApplied()) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const theme = document.querySelector('cbar-theme');
    let settled = false;

    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      theme?.removeEventListener('cbar-theme-applied', onThemeEvent);
      window.removeEventListener('cbar-theme-updated', onThemeEvent);
      window.clearInterval(pollId);
      window.clearTimeout(timeoutId);
      resolve();
    };

    const onThemeEvent = () => {
      if (isThemeApplied()) {
        finish();
      }
    };

    theme?.addEventListener('cbar-theme-applied', onThemeEvent);
    window.addEventListener('cbar-theme-updated', onThemeEvent);

    const pollId = window.setInterval(() => {
      if (isThemeApplied()) {
        finish();
      }
    }, 16);

    const timeoutId = window.setTimeout(finish, timeoutMs);
  });
}

function waitForDrawer(shell: CbarShellElement, maxAttempts = 50): Promise<void> {
  return new Promise((resolve) => {
    let attempts = 0;

    const tryFind = () => {
      const drawer = shell.shadowRoot?.querySelector('cbar-drawer');
      if (drawer?.shadowRoot) {
        resolve();
        return;
      }

      attempts += 1;
      if (attempts >= maxAttempts) {
        resolve();
        return;
      }

      requestAnimationFrame(tryFind);
    };

    tryFind();
  });
}

function getAdminHeaderHeight(shell: CbarShellElement): number {
  const header = shell.shadowRoot?.querySelector('#header') as HTMLElement | null;

  if (!header) {
    return 64;
  }

  return header.getBoundingClientRect().height || 64;
}

function getAdminTabsHeight(shell: CbarShellElement): number {
  const tabsDiv = shell.shadowRoot?.querySelector('#tabs-div') as HTMLElement | null;

  if (!tabsDiv || tabsDiv.hasAttribute('hidden')) {
    return 0;
  }

  return tabsDiv.getBoundingClientRect().height;
}

/** Main content offset: header + tabs (tabs live in main-content, not over the drawer). */
function getAdminHeaderOffset(shell: CbarShellElement): number {
  return getAdminHeaderHeight(shell) + getAdminTabsHeight(shell);
}

function applyAdminHeaderActionsSpacing(shell: CbarShellElement): boolean {
  const header = shell.shadowRoot?.querySelector('cbar-header#header');
  if (!header?.shadowRoot) {
    return false;
  }

  const styleId = 'admin-header-actions-spacing';
  let style = header.shadowRoot.querySelector(`#${styleId}`) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = styleId;
    header.shadowRoot.appendChild(style);
  }

  style.textContent = `
    .header-bar .actions {
      gap: var(--cbar-sys-spacing-2, 8px);
      padding-inline: var(--cbar-sys-spacing-2, 8px) var(--cbar-sys-spacing-4, 16px);
    }

    .header-bar .actions cbar-icon-button,
    .header-bar .actions .menu-container {
      margin: 0;
    }

    .header-bar .actions cbar-image.person-image {
      margin: 0;
    }
  `;

  return true;
}

function applyAdminShellHeaderLayoutFix(shell: CbarShellElement): void {
  if (!shell.shadowRoot) {
    return;
  }

  const styleId = 'admin-shell-header-layout-fix';
  let style = shell.shadowRoot.querySelector(`#${styleId}`) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = styleId;
    shell.shadowRoot.appendChild(style);
  }

  style.textContent = `
    .shell-layout {
      overflow-x: hidden;
      overflow-y: visible;
    }
    .main-content {
      overflow-x: hidden;
      overflow-y: visible;
    }
    cbar-header#header {
      flex-shrink: 0;
      z-index: 100;
    }
    #tabs-div:not([hidden]) {
      position: sticky;
      top: var(--cbar-header-height, 64px);
      z-index: 90;
      background-color: var(--cbar-sys-color-surface);
    }
  `;
}

function syncAdminHeaderSpacer(shell: CbarShellElement): boolean {
  const spacer = shell.shadowRoot?.querySelector('#header-spacer') as HTMLElement | null;
  const header = shell.shadowRoot?.querySelector('#header') as HTMLElement | null;

  if (!spacer || !header) {
    return false;
  }

  const headerHeight = getAdminHeaderHeight(shell);
  const offset = getAdminHeaderOffset(shell);

  // cbar-shell adds tabs height to this spacer, but #tabs-div is a sibling
  // below it — reserve header height only so tabs sit flush under the bar.
  spacer.style.height = `${headerHeight}px`;
  shell.style.setProperty('--admin-header-offset', `${offset}px`);
  shell.style.setProperty('--admin-drawer-offset', `${headerHeight}px`);
  return true;
}

export function refreshShellHeaderLayout(shell: CbarShellElement): void {
  syncAdminHeaderSpacer(shell);
  applyAdminHeaderActionsSpacing(shell);
}

function observeAdminHeaderSpacer(shell: CbarShellElement): void {
  const header = shell.shadowRoot?.querySelector('#header');
  const tabsDiv = shell.shadowRoot?.querySelector('#tabs-div');

  if (!header) {
    return;
  }

  const update = () => {
    syncAdminHeaderSpacer(shell);
    applyAdminDrawerStyles(shell);
    applyAdminHeaderActionsSpacing(shell);
  };

  const observer = new ResizeObserver(update);
  observer.observe(header);
  if (tabsDiv) {
    observer.observe(tabsDiv);
  }

  update();
}

const ADMIN_SHELL_DRAWER_WIDTH = '320px';
const ACCOUNT_SHELL_DRAWER_WIDTH = '200px';

function getShellDrawerWidth(shell: CbarShellElement): string {
  return shell.id === 'app-shell' ? ACCOUNT_SHELL_DRAWER_WIDTH : ADMIN_SHELL_DRAWER_WIDTH;
}

function getShellDrawerModalWidth(shell: CbarShellElement): string {
  return shell.id === 'app-shell' ? 'min(280px, 100vw)' : 'min(320px, 100vw)';
}

function applyAdminDrawerStyles(shell: CbarShellElement): boolean {
  const drawer = shell.shadowRoot?.querySelector('cbar-drawer');
  if (!drawer?.shadowRoot) {
    return false;
  }

  const drawerOffset = getAdminHeaderHeight(shell);
  const drawerWidth = getShellDrawerWidth(shell);
  const drawerModalWidth = getShellDrawerModalWidth(shell);

  shell.style.setProperty('--admin-drawer-width', drawerWidth);

  if (!drawer.shadowRoot.querySelector('#admin-drawer-styles')) {
    const style = document.createElement('style');
    style.id = 'admin-drawer-styles';
    drawer.shadowRoot.appendChild(style);
  }

  const style = drawer.shadowRoot.querySelector('#admin-drawer-styles') as HTMLStyleElement;
  style.textContent = `
      .drawer[data-type="standard"] .drawer-toolbar {
        display: none;
      }

      .drawer {
        width: min(${drawerWidth}, 85vw);
        max-width: 100%;
      }

      .drawer[data-type="standard"] {
        height: calc(100vh - ${drawerOffset}px);
        margin-top: ${drawerOffset}px;
        flex-shrink: 0;
      }

      .drawer[data-type="modal"] {
        top: ${drawerOffset}px;
        height: calc(100vh - ${drawerOffset}px);
        width: ${drawerModalWidth};
      }

      .drawer-scrim {
        top: ${drawerOffset}px;
      }

      #drawer-contents::slotted(*) {
        min-height: 0;
        -webkit-overflow-scrolling: touch;
      }
    `;

  return true;
}

function syncShellDrawerVisibility(shell: CbarShellElement): void {
  shell.showDrawer = shell.dataset.showDrawer !== 'false';
}

async function revealAdminShell(shell: CbarShellElement) {
  await Promise.all([
    customElements.whenDefined('cbar-shell'),
    customElements.whenDefined('cbar-drawer'),
    customElements.whenDefined('cbar-list'),
    customElements.whenDefined('cbar-list-item'),
    waitForThemeApplied(),
  ]);

  await waitForDrawer(shell);

  syncShellDrawerVisibility(shell);

  const serverLoggedIn = shell.hasAttribute('logged-in');
  const username = shell.dataset.username ?? '';
  const profileImageUrl = shell.dataset.profileImageUrl ?? '';

  if (username || profileImageUrl) {
    shell.user = {
      profile: {
        name: username,
        picture: profileImageUrl,
      },
    };
  }

  if (serverLoggedIn) {
    shell.loggedIn = true;
  }

  if (shell.header && shell.dataset.searchPlaceholder) {
    shell.header.searchPlaceholder = shell.dataset.searchPlaceholder;
  }

  shell.loading = false;
  applyAdminShellHeaderLayoutFix(shell);
  applyAdminHeaderActionsSpacing(shell);

  requestAnimationFrame(() => {
    observeAdminHeaderSpacer(shell);
    setupLogoutHandler();
  });

  shell.classList.add('admin-shell-ready');
  document.body.classList.add('admin-theme-ready');
  document.body.removeAttribute('unresolved');
}

export function setupAppShell() {
  const shell = document.querySelector('#app-shell, #admin-shell') as CbarShellElement | null;

  if (!shell) {
    return;
  }

  // cbar-shell only renders the header-tabs slot when loggedIn is true.
  if (shell.hasAttribute('logged-in')) {
    shell.loggedIn = true;
  }

  shell.showNotifications = false;
  shell.showChat = false;
  shell.showServiceWorkerToast = false;
  shell.showOfflineToast = false;

  // Account layout: search redirects to admin-only routes and is not useful here.
  if (shell.id === 'app-shell') {
    shell.showSearch = false;
  }

  void revealAdminShell(shell);
}

/** @deprecated Use setupAppShell */
export const setupAdminShell = setupAppShell;
